<script setup lang="ts">
/**
 * Kleine knop die een waarde naar het klembord kopieert (bv. een ID of e-mailadres).
 *
 *   <VCopyButton :value="String(model.id)" />
 *
 * Quality of life (vraag klant 25/09/2026): ID's en e-mailadressen werden met de hand
 * geselecteerd. Na het kopiëren wordt het icoon even een vinkje. Eén component i.p.v.
 * de losse navigator.clipboard-knoppen per scherm.
 */
import { IconCheck, IconCopy } from '@tabler/icons-vue'
import { trans } from 'laravel-vue-i18n'
import { onBeforeUnmount, ref } from 'vue'

const props = defineProps<{
    value: string | number | bigint | null | undefined
}>()

const copied = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

async function copy(): Promise<void> {
    if (props.value === null || props.value === undefined || props.value === '') {
        return
    }
    try {
        await navigator.clipboard.writeText(String(props.value))
        copied.value = true
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(() => (copied.value = false), 1500)
    } catch {
        // Klembord geweigerd (geen https of geen toestemming): stil falen, de waarde
        // blijft zichtbaar en selecteerbaar.
    }
}

onBeforeUnmount(() => {
    if (timer) {
        clearTimeout(timer)
    }
})
</script>

<template>
    <button
        type="button"
        class="v-copy-button inline-flex shrink-0 items-center justify-center rounded p-0.5 text-gray-400 transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-500"
        :aria-label="copied ? trans('global.copied') : trans('global.copy')"
        :title="copied ? trans('global.copied') : trans('global.copy')"
        @click.stop.prevent="copy"
    >
        <IconCheck v-if="copied" class="no-global h-4 w-4 text-green-600" aria-hidden="true" />
        <IconCopy v-else class="no-global h-4 w-4" aria-hidden="true" />
    </button>
</template>
